// Union member web: members booking and scoring at their club, in a browser, phone first.
// The club comes from the subdomain: {club}.unionclubs.com (locally,
// https://{club}.localhost:5174). Members sign in once at the shared sign-in site; each club
// site gets its own session from it (club-session.ts). Built on @union/ui (DESIGN.md):
// AppShell with bottom tabs on phones.

import "@union/ui/styles.css";
import {
  QueryClient,
  QueryClientProvider,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  type Booking,
  clubSlugFromHost,
  type Me,
  type MemberTeeSheet,
  type MemberTeeTime,
  type PlayOption,
} from "@union/contracts";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@union/ui/components/alert-dialog";
import { Badge } from "@union/ui/components/badge";
import { Button } from "@union/ui/components/button";
import { Card } from "@union/ui/components/card";
import { Input } from "@union/ui/components/input";
import { Pennant } from "@union/ui/components/pennant";
import { PlayOptionIcon } from "@union/ui/components/play-option-icon";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@union/ui/components/select";
import { Skeleton } from "@union/ui/components/skeleton";
import { Toaster } from "@union/ui/components/sonner";
import { Tabs, TabsList, TabsTrigger } from "@union/ui/components/tabs";
import { TooltipProvider } from "@union/ui/components/tooltip";
import { type AppNavItem, AppShell } from "@union/ui/layouts/app-shell";
import { AuthLayout } from "@union/ui/layouts/auth-layout";
import { cn } from "@union/ui/lib/utils";
import {
  ApiRequestError,
  ApiUnreachableError,
  apiGet,
  apiPost,
  apiSend,
  todayAt,
  useTeeSheetEvents,
} from "@union/web-kit";
import { ErrorState } from "@union/web-kit/ui";
import {
  ArrowRightLeft,
  CalendarRange,
  Clock,
  Flag,
  Lock,
  MessageSquareText,
  Ticket,
  UserPlus,
  X,
} from "lucide-react";
import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { toast } from "sonner";
import {
  type ClubSignInState,
  ensureClubSession,
  signInAgain,
  signOutEverywhere,
  useClubSession,
} from "./club-session.ts";
import { LotteryPanel } from "./lottery.tsx";
import { ScorecardScreen, StartRound } from "./scoring/screens.tsx";

const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 15_000, retry: 1 } } });
const clubSlug = clubSlugFromHost(window.location.host, import.meta.env.VITE_ROOT_DOMAIN);

const readHashPath = () => window.location.hash.replace(/^#/, "") || "/";

/** The part of the URL after #, e.g. "/score/abc". Survives offline reloads. */
function useHashPath(): [string, (path: string) => void] {
  const [path, setPath] = useState(readHashPath);
  useEffect(() => {
    const onChange = () => setPath(readHashPath());
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return [path, (next) => (window.location.hash = next)];
}

const SIGN_IN_PROBLEMS: Record<Exclude<ClubSignInState, "signed-in" | "redirecting">, string> = {
  "no-such-club": "There’s no Union club at this address.",
  offline: "Can’t reach Union to sign you in. Check your connection and reload.",
  failed: "Couldn’t sign you in. Reload to try again.",
};

function App() {
  const [path, go] = useHashPath();
  const [signIn, setSignIn] = useState<ClubSignInState | "checking">("checking");
  useEffect(() => {
    // No club in the address: the sign-in site lists the member's clubs.
    if (!clubSlug) window.location.replace(import.meta.env.VITE_AUTH_ORIGIN);
    else ensureClubSession(clubSlug).then(setSignIn);
  }, []);

  if (!clubSlug || signIn === "checking" || signIn === "redirecting") return null;
  if (signIn !== "signed-in") {
    return (
      <AuthLayout eyebrow="Union Clubs" title="Can’t open this club" description={SIGN_IN_PROBLEMS[signIn]}>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Reload
        </Button>
      </AuthLayout>
    );
  }
  return <MemberShell slug={clubSlug} path={path} go={go} />;
}

function useMe() {
  const session = useClubSession();
  return useQuery({
    queryKey: ["me"],
    queryFn: () => apiGet<Me>("/me", session ?? fail()),
    enabled: !!session,
  });
}

/** Another club's member site: the same address with its slug in place of this one. */
const clubSite = (slug: string) => window.location.origin.replace(`//${clubSlug}.`, `//${slug}.`);

function MemberShell({ slug, path, go }: { slug: string; path: string; go: (path: string) => void }) {
  const me = useMe();
  const memberships = me.data?.memberships ?? [];
  const here = memberships.find((m) => m.club.slug === slug);
  const [date, setDate] = useState<string | undefined>(undefined);
  const day = date ?? (here ? todayAt(here.club.timezone) : undefined);

  const scoring = path.startsWith("/score");
  const roundId = path.match(/^\/score\/([0-9a-f-]{36})$/)?.[1];
  const nav: AppNavItem[] = [
    { title: "Tee times", href: "#/", icon: CalendarRange, active: !scoring },
    { title: "Score", href: "#/score", icon: Flag, active: scoring },
  ];

  return (
    <AppShell
      product="Union"
      {...(here
        ? {
            context: {
              label: "Your clubs",
              current: { id: here.club.slug, name: here.club.name, detail: `Member ${here.memberNumber}` },
              options: memberships.map((m) => ({
                id: m.club.slug,
                name: m.club.name,
                detail: `Member ${m.memberNumber}`,
              })),
              onSelect: (other: string) => {
                if (other !== slug) window.location.assign(clubSite(other));
              },
            },
          }
        : {})}
      nav={[{ items: nav }]}
      mobileTabs={nav}
      user={{ name: me.data?.displayName ?? "", onSignOut: () => signOutEverywhere() }}
      breadcrumbs={[{ label: scoring ? "Score" : "Tee times" }]}
      actions={
        !scoring && day ? (
          <Input
            type="date"
            data-size="sm"
            aria-label="Date"
            className="w-auto"
            value={day}
            onChange={(e) => e.target.value && setDate(e.target.value)}
          />
        ) : undefined
      }
    >
      {/* Scoring works with no signal, so only the tee sheet waits on /me. */}
      {roundId ? (
        <ScorecardScreen roundId={roundId} />
      ) : scoring ? (
        <StartRound slug={slug} go={go} />
      ) : me.isError ? (
        <ErrorState
          what="your clubs"
          error={me.error}
          onRetry={() => me.refetch()}
          onSignIn={() => signInAgain(slug)}
        />
      ) : me.data && !here ? (
        <p className="type-body text-muted-foreground">You aren’t a member of this club.</p>
      ) : (
        here && day && <ClubTeeSheet slug={slug} clubId={here.club.id} day={day} />
      )}
    </AppShell>
  );
}

function ClubTeeSheet({ slug, clubId, day }: { slug: string; clubId: string; day: string }) {
  const session = useClubSession();
  const sheet = useQuery({
    queryKey: ["member-tee-sheet", slug, day],
    queryFn: () => apiGet<MemberTeeSheet>(`/member/clubs/${slug}/tee-sheet?date=${day}`, session ?? fail()),
    enabled: !!session,
  });

  // Live: any booking change at this club (yours or anyone's) refetches.
  const queryClient = useQueryClient();
  useTeeSheetEvents(
    clubId,
    () => queryClient.invalidateQueries({ queryKey: ["member-tee-sheet", slug] }),
    session?.access_token,
  );

  // One course or simulator at a time, so it's clear which one you're booking.
  const courses = sheet.data?.courses ?? [];
  const [picked, setPicked] = useState<string | null>(() => readCourse(slug));
  const courseId = courses.find((c) => c.id === picked)?.id ?? courses[0]?.id;
  const allTimes = sheet.data?.teeTimes ?? [];
  const times = allTimes.filter((t) => t.courseId === courseId);
  // Your time that day, on any course.
  const mine = allTimes.find((t) => t.players.some((p) => p.isMe));
  return (
    <>
      <h1 className="type-display-l max-sm:type-heading">{formatDay(day)}</h1>
      {sheet.isError && (
        <ErrorState
          what="the tee sheet"
          error={sheet.error}
          onRetry={() => sheet.refetch()}
          onSignIn={() => signInAgain(slug)}
        />
      )}
      {sheet.isPending && (
        <div role="status" aria-label="Loading the tee sheet" className="grid gap-space-2">
          {["a", "b", "c", "d", "e"].map((k) => (
            <Skeleton key={k} className="h-row-comfy w-full" />
          ))}
        </div>
      )}
      {mine && (
        <Card className="flex-row items-center gap-space-3 px-space-5 py-space-4">
          <Pennant size="lg" />
          <div>
            <p className="type-title">You’re on the {mine.localTime}</p>
            <p className="type-caption text-muted-foreground">
              {mine.courseName}
              {mine.players.length > 1 &&
                ` · with ${mine.players
                  .filter((p) => !p.isMe)
                  .map((p) => p.displayName)
                  .join(", ")}`}
            </p>
          </div>
        </Card>
      )}
      {courses.length > 1 && courseId && (
        <Tabs
          value={courseId}
          onValueChange={(id) => {
            setPicked(id);
            rememberCourse(slug, id);
          }}
        >
          <TabsList variant="pill" aria-label="Course">
            {courses.map((c) => (
              <TabsTrigger key={c.id} variant="pill" value={c.id}>
                {c.name}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}
      {/* Lottery days (F1–F2): request a time instead of booking, until the reveal. */}
      {sheet.data?.lotteries
        .filter((l) => l.courseId === courseId)
        .map((l) => (
          <LotteryPanel
            key={l.courseId}
            slug={slug}
            date={day}
            status={l}
            courseName={times.find((t) => t.courseId === l.courseId)?.courseName ?? "this course"}
            times={times.filter((t) => t.courseId === l.courseId).map((t) => t.localTime)}
            timezone={sheet.data.club.timezone}
          />
        ))}
      {sheet.data && times.length === 0 && (
        <p className="type-body text-muted-foreground">No tee times on this day.</p>
      )}
      {times.length > 0 && (
        <Card className="gap-0 overflow-hidden py-0" data-testid="tee-sheet">
          {times.map((t) => (
            <TeeTimeRow key={t.id} slug={slug} teeTime={t} playOptions={sheet.data?.playOptions ?? []} />
          ))}
        </Card>
      )}
    </>
  );
}

/** One tee time, member density: time, who's playing, places left, and Book. */
function TeeTimeRow({
  slug,
  teeTime: t,
  playOptions,
}: {
  slug: string;
  teeTime: MemberTeeTime;
  playOptions: PlayOption[];
}) {
  const isMine = t.players.some((p) => p.isMe);
  const open = t.blocked ? 0 : t.maxPlayers - t.players.length;
  const names = t.players.map((p) => (p.isMe ? "You" : p.displayName)).join(", ");
  // Started already: shown for the record, nothing to do on it.
  const past = t.restriction?.code === "already_started";
  return (
    <div
      data-testid="tee-time"
      data-open={open}
      className={cn(
        "flex min-h-row-comfy items-center gap-space-4 border-b border-border px-space-4 py-space-2 last:border-b-0 even:bg-stripe",
        isMine && "!bg-primary-soft",
        past && "text-muted-foreground",
      )}
    >
      <span className="flex w-16 shrink-0 items-center gap-space-1 type-time">
        {isMine && <Pennant label="Your tee time" />}
        {t.localTime}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate type-body">
          {names || <span className="text-muted-foreground">Nobody yet</span>}
        </p>
        <p className="type-caption text-muted-foreground">
          {past
            ? "Teed off"
            : t.blocked
              ? (t.eventName ?? "Not available")
              : open === 0
                ? "Full"
                : `${open} open`}{" "}
          · {t.courseName}
        </p>
        {/* Your own time: how you're playing (G2), changeable until it starts. */}
        {isMine && !past && t.myBookingId && playOptions.length > 0 && (
          <PlayOptionPicker
            slug={slug}
            bookingId={t.myBookingId}
            value={t.myPlayOptionId}
            options={playOptions}
          />
        )}
        {isMine && !past && t.myBookingId && (
          <NoteEditor slug={slug} bookingId={t.myBookingId} note={t.myNote} />
        )}
        {/* Your own time: the guests you bring (E7), within the club's guest rules. */}
        {isMine && !past && t.myBookingId && (
          <GuestsEditor slug={slug} teeTime={t} playOptions={playOptions} />
        )}
        {/* Your group's rules don't let you book this time: say why (E4). */}
        {t.restriction && !past && !isMine && !t.blocked && open > 0 && (
          <p
            className="flex items-center gap-space-1 type-caption text-muted-foreground"
            data-testid="restriction"
          >
            {t.restriction.code === "window_not_open" ? (
              <Clock aria-hidden="true" className="size-3.5 shrink-0" />
            ) : t.restriction.code === "lottery" ? (
              <Ticket aria-hidden="true" className="size-3.5 shrink-0" />
            ) : (
              <Lock aria-hidden="true" className="size-3.5 shrink-0" />
            )}
            {t.restriction.message}
          </p>
        )}
      </div>
      {isMine ? (
        t.myBookingId && !past ? (
          <RemoveMeButton slug={slug} teeTime={t} bookingId={t.myBookingId} />
        ) : (
          <Badge variant="primary">You’re on it</Badge>
        )
      ) : past || t.blocked || open === 0 ? null : t.restriction?.switchFrom ? (
        // Too close to your own booking that day: move that one here instead (E5).
        <SwitchButton slug={slug} teeTime={t} from={t.restriction.switchFrom} />
      ) : t.restriction ? null : (
        <BookButton slug={slug} teeTime={t} />
      )}
    </div>
  );
}

const CONFLICTS: Record<string, string> = {
  tee_time_full: "Just filled up",
  already_booked: "You’re already on it",
  window_not_open: "Not open for booking yet",
  not_for_group: "Not for your membership",
  too_close: "Too close to your other time",
  already_started: "Teed off already",
  lottery: "Lottery day: not open for booking yet",
  tee_time_blocked: "No longer available",
};

/**
 * Books the signed-in member onto `teeTime`. One Idempotency-Key per booking attempt: "Try
 * again" resends the SAME key, so if the first attempt actually went through before the
 * connection dropped, the retry returns that booking rather than trying a second time.
 */
function BookButton({ slug, teeTime }: { slug: string; teeTime: MemberTeeTime }) {
  const session = useClubSession();
  const queryClient = useQueryClient();
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const book = useMutation({
    mutationFn: () =>
      apiPost<Booking>(`/member/clubs/${slug}/tee-times/${teeTime.id}/bookings`, session ?? fail(), {
        idempotencyKey,
      }),
    onSuccess: () =>
      toast.success(`You’re on the ${teeTime.localTime} on ${teeTime.courseName}. See you there.`),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["member-tee-sheet", slug] }),
  });

  const error = book.error;
  if (error instanceof ApiRequestError && error.status === 409) {
    return <Badge variant="warning">{CONFLICTS[error.code] ?? "Couldn’t book this time"}</Badge>;
  }
  if (error instanceof ApiRequestError && error.status === 401) {
    return (
      <Button size="sm" variant="outline" onClick={() => signInAgain(slug)}>
        Sign in again
      </Button>
    );
  }
  if (error) {
    return (
      <Button
        size="sm"
        variant="outline"
        disabled={book.isPending}
        title={error instanceof ApiUnreachableError ? "Can’t reach Union. Check your connection." : undefined}
        onClick={() => book.mutate()}
      >
        {book.isPending ? "Trying…" : "Try again"}
      </Button>
    );
  }
  if (teeTime.players.length >= teeTime.maxPlayers) return null;
  return (
    <Button size="sm" disabled={book.isPending} onClick={() => book.mutate()}>
      {book.isPending ? "Booking…" : "Book"}
    </Button>
  );
}

/** How the member is playing on their own booking (G2): saved as soon as they choose. */
function PlayOptionPicker({
  slug,
  bookingId,
  value,
  options,
  label = "Playing",
}: {
  slug: string;
  bookingId: string;
  value: string | null;
  options: PlayOption[];
  /** Who it's for, when it isn't the member ("Sam Rivera is playing"). */
  label?: string;
}) {
  const session = useClubSession();
  const queryClient = useQueryClient();
  const set = useMutation({
    mutationFn: (playOptionId: string) =>
      apiSend<{ id: string }>(
        "PUT",
        `/member/clubs/${slug}/bookings/${bookingId}/play-option`,
        session ?? fail(),
        {
          body: { playOptionId },
        },
      ),
    onSuccess: (_, playOptionId) =>
      toast.success(`Got it: ${options.find((o) => o.id === playOptionId)?.name.toLowerCase() ?? "saved"}.`),
    onError: (error) =>
      toast.error(
        error instanceof ApiRequestError && error.status === 409 && error.detail
          ? error.detail
          : "Couldn’t save that. Try again.",
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["member-tee-sheet", slug] }),
  });
  return (
    <div className="mt-space-1 flex items-center gap-space-2">
      <span className="whitespace-nowrap type-caption text-muted-foreground" id={`play-${bookingId}`}>
        {label}
      </span>
      <Select value={value ?? ""} onValueChange={(v) => set.mutate(v)} disabled={set.isPending}>
        <SelectTrigger size="sm" className="w-40" aria-labelledby={`play-${bookingId}`}>
          <SelectValue placeholder="Choose…" />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.id} value={o.id}>
              <PlayOptionIcon name={o.name} />
              {o.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** The member's note on their own booking, for the starter (G3). Only they and staff see it. */
function NoteEditor({ slug, bookingId, note }: { slug: string; bookingId: string; note: string | null }) {
  const session = useClubSession();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(note ?? "");
  const save = useMutation({
    mutationFn: () =>
      apiSend<{ id: string; note: string | null }>(
        "PUT",
        `/member/clubs/${slug}/bookings/${bookingId}/note`,
        session ?? fail(),
        { body: { note: value } },
      ),
    onSuccess: (saved) => {
      setEditing(false);
      toast.success(saved.note ? "Note saved. The starter will see it." : "Note removed.");
    },
    onError: (error) =>
      toast.error(
        error instanceof ApiRequestError && error.status === 409 && error.detail
          ? error.detail
          : "Couldn’t save your note. Try again.",
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["member-tee-sheet", slug] }),
  });

  if (!editing) {
    return (
      <div className="mt-space-1 flex min-w-0 items-center gap-space-1 type-caption text-muted-foreground">
        <MessageSquareText aria-hidden="true" className="size-3.5 shrink-0" />
        {note && <span className="truncate">{note}</span>}
        <Button
          variant="link"
          size="sm"
          className="h-auto px-0"
          onClick={() => {
            setValue(note ?? "");
            setEditing(true);
          }}
        >
          {note ? "Edit" : "Add a note for the starter"}
        </Button>
      </div>
    );
  }
  return (
    <form
      className="mt-space-1 flex flex-wrap items-center gap-space-2"
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      <Input
        data-size="sm"
        className="min-w-48 flex-1"
        aria-label="Note for the starter"
        placeholder="e.g. playing 9 holes"
        maxLength={200}
        value={value}
        // The member just asked to write it.
        autoFocus
        onChange={(e) => setValue(e.target.value)}
      />
      <Button type="submit" size="sm" disabled={save.isPending}>
        {save.isPending ? "Saving…" : "Save"}
      </Button>
      <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
        Cancel
      </Button>
    </form>
  );
}

/** The guests a member brings on their own time: add one (name, email if known), or take one off. */
function GuestsEditor({
  slug,
  teeTime: t,
  playOptions,
}: {
  slug: string;
  teeTime: MemberTeeTime;
  playOptions: PlayOption[];
}) {
  const session = useClubSession();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  // How the guest is playing (G2): the club's default until the member picks.
  const defaultOption = playOptions.find((o) => o.isDefault)?.id ?? "";
  const [playOptionId, setPlayOptionId] = useState(defaultOption);
  const done = () => queryClient.invalidateQueries({ queryKey: ["member-tee-sheet", slug] });
  // The club's guest rules say why, in words for the member (409).
  const problem = (error: Error, fallback: string) =>
    error instanceof ApiRequestError && error.status === 409 && error.detail ? error.detail : fallback;
  const add = useMutation({
    mutationFn: () =>
      apiSend("POST", `/member/clubs/${slug}/tee-times/${t.id}/guests`, session ?? fail(), {
        body: {
          name: name.trim(),
          ...(email.trim() ? { email: email.trim() } : {}),
          ...(playOptionId ? { playOptionId } : {}),
        },
      }),
    onSuccess: () => {
      toast.success(`${name.trim()} is on the ${t.localTime} with you.`);
      setAdding(false);
      setName("");
      setEmail("");
      setPlayOptionId(defaultOption);
    },
    onError: (error) => toast.error(problem(error, "Couldn’t add your guest. Try again.")),
    onSettled: done,
  });
  const remove = useMutation({
    mutationFn: (guest: { bookingId: string; name: string }) =>
      apiSend("DELETE", `/member/clubs/${slug}/bookings/${guest.bookingId}`, session ?? fail()),
    onSuccess: (_, guest) => toast.success(`${guest.name} is off the ${t.localTime}.`),
    onError: (error) => toast.error(problem(error, "Couldn’t take your guest off. Try again.")),
    onSettled: done,
  });
  const room = t.maxPlayers - t.players.length > 0;

  return (
    <div className="mt-space-1 grid gap-space-1">
      {t.myGuests.length > 0 && (
        <ul className="grid gap-space-1" aria-label="Your guests">
          {t.myGuests.map((g) => (
            <li key={g.bookingId} className="flex items-center gap-space-1">
              <UserPlus aria-hidden="true" className="mt-space-1 size-3.5 shrink-0 text-muted-foreground" />
              {playOptions.length > 0 ? (
                <PlayOptionPicker
                  slug={slug}
                  bookingId={g.bookingId}
                  value={g.playOptionId}
                  options={playOptions}
                  label={g.name}
                />
              ) : (
                <span className="type-caption">{g.name}</span>
              )}
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={`Take ${g.name} off`}
                disabled={remove.isPending}
                onClick={() => remove.mutate(g)}
              >
                <X aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      )}
      {adding ? (
        <form
          className="flex flex-wrap items-center gap-space-2"
          onSubmit={(event) => {
            event.preventDefault();
            add.mutate();
          }}
        >
          <Input
            data-size="sm"
            className="min-w-40 flex-1"
            aria-label="Guest’s name"
            placeholder="Guest’s name"
            maxLength={80}
            value={name}
            // The member just asked to add one.
            autoFocus
            required
            onChange={(e) => setName(e.target.value)}
          />
          <Input
            data-size="sm"
            type="email"
            className="min-w-48 flex-1"
            aria-label="Guest’s email (optional)"
            placeholder="Email (optional)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {playOptions.length > 0 && (
            <Select value={playOptionId} onValueChange={setPlayOptionId}>
              <SelectTrigger size="sm" className="w-40" aria-label="How your guest is playing">
                <SelectValue placeholder="Playing…" />
              </SelectTrigger>
              <SelectContent>
                {playOptions.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    <PlayOptionIcon name={o.name} />
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button type="submit" size="sm" disabled={add.isPending || !name.trim()}>
            {add.isPending ? "Adding…" : "Add guest"}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setAdding(false)}>
            Cancel
          </Button>
        </form>
      ) : (
        room && (
          <Button
            variant="link"
            size="sm"
            className="h-auto justify-self-start px-0"
            onClick={() => setAdding(true)}
          >
            <UserPlus aria-hidden="true" /> Add a guest
          </Button>
        )
      )}
    </div>
  );
}

/** Takes the member off a tee time they're on, after a confirmation. */
function RemoveMeButton({
  slug,
  teeTime,
  bookingId,
}: {
  slug: string;
  teeTime: MemberTeeTime;
  bookingId: string;
}) {
  const session = useClubSession();
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () =>
      apiSend<{ id: string }>("DELETE", `/member/clubs/${slug}/bookings/${bookingId}`, session ?? fail()),
    onSuccess: () => toast.success(`You’re off the ${teeTime.localTime}. Your place is open again.`),
    onError: (error) =>
      toast.error(
        error instanceof ApiRequestError && error.status === 409 && error.detail
          ? error.detail
          : "Couldn’t take you off this time. Try again.",
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["member-tee-sheet", slug] }),
  });
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button size="sm" variant="outline" disabled={remove.isPending}>
          {remove.isPending ? "Removing…" : "Remove me"}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Take yourself off the {teeTime.localTime}?</AlertDialogTitle>
          <AlertDialogDescription>
            Your place goes back on the tee sheet for anyone to book
            {teeTime.myGuests.length > 0 &&
              `, and your ${teeTime.myGuests.length === 1 ? "guest comes" : "guests come"} off with you`}
            . You can book it again if it’s still open.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep my time</AlertDialogCancel>
          <AlertDialogAction variant="destructive" onClick={() => remove.mutate()}>
            Remove me
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** Moves the member's own booking `from` to `teeTime` in one step (07:27 → 08:12). */
function SwitchButton({
  slug,
  teeTime,
  from,
}: {
  slug: string;
  teeTime: MemberTeeTime;
  from: { bookingId: string; localTime: string };
}) {
  const session = useClubSession();
  const queryClient = useQueryClient();
  const move = useMutation({
    mutationFn: () =>
      apiSend<Booking>(
        "PUT",
        `/member/clubs/${slug}/bookings/${from.bookingId}/tee-time`,
        session ?? fail(),
        {
          body: { teeTimeId: teeTime.id },
        },
      ),
    onSuccess: () =>
      toast.success(`You’re now on the ${teeTime.localTime} instead of the ${from.localTime}.`),
    onError: (error) =>
      toast.error(
        error instanceof ApiRequestError && error.status === 409 && error.detail
          ? error.detail
          : "Couldn’t switch your time. Try again.",
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["member-tee-sheet", slug] }),
  });
  return (
    <Button
      size="sm"
      variant="outline"
      disabled={move.isPending}
      aria-label={`Switch from ${from.localTime} to ${teeTime.localTime}`}
      onClick={() => move.mutate()}
    >
      <ArrowRightLeft /> {move.isPending ? "Switching…" : `Switch from ${from.localTime}`}
    </Button>
  );
}

/** The course last shown at this club, so the tee sheet opens where you left it. */
const courseKey = (slug: string) => `union:member-course:${slug}`;
function readCourse(slug: string): string | null {
  try {
    return localStorage.getItem(courseKey(slug));
  } catch {
    return null;
  }
}
function rememberCourse(slug: string, courseId: string) {
  try {
    localStorage.setItem(courseKey(slug), courseId);
  } catch {
    // Private browsing or storage off: it just won't be remembered.
  }
}

function formatDay(date: string): string {
  // Format the calendar date itself; no timezone conversion.
  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${date}T00:00:00Z`));
}

function fail(): never {
  throw new Error("session required");
}

const root = document.getElementById("root");
if (!root) throw new Error("#root missing");
// Keeps the app loadable with no signal (see public/sw.js). Production builds only: in
// development it would serve stale code.
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  navigator.serviceWorker
    .register("/sw.js")
    .catch((error: unknown) =>
      console.warn("Offline support unavailable: service worker failed to register", error),
    );
}

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <App />
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  </StrictMode>,
);
